export enum LessonType {
  Private = 'private',
  Group = 'group',
}

export enum LessonStatus {
  Scheduled = 'scheduled',
  Cancelled = 'cancelled',
  Completed = 'completed',
}

export enum ReceiptStatus {
  Paid = 'paid',
  Cancelled = 'cancelled',
}

export enum PaymentType {
  Cash = 'cash',
  Card = 'card',
  Invoice = 'invoice',
}

export enum LessonPaymentStatus {
  Unpaid = 'unpaid',
  Paid = 'paid',
  PartiallyPaid = 'partially_paid',
}

export enum LanguageLevel {
  None = 'none',
  Basic = 'basic',
  Intermediate = 'intermediate',
  Proficient = 'proficient',
}

export enum LicenceLevel {
  None = 'none',
  ApulA = 'apulA',
  ApulB = 'apulB',
  ApulC = 'apulC',
  ApulD = 'apulD',
}

export const LANGUAGE_CODES = ['cz', 'en', 'de', 'pl'] as const
export type LanguageCode = (typeof LANGUAGE_CODES)[number]

export const LICENCE_DISCIPLINES = ['ski', 'snowboard'] as const
export type LicenceDiscipline = (typeof LICENCE_DISCIPLINES)[number]

export type InstructorLanguages = Record<LanguageCode, LanguageLevel>
export type InstructorLicences = Record<LicenceDiscipline, LicenceLevel>

export const DEFAULT_LANGUAGES: InstructorLanguages = {
  cz: LanguageLevel.None,
  en: LanguageLevel.None,
  de: LanguageLevel.None,
  pl: LanguageLevel.None,
}

export const DEFAULT_LICENCES: InstructorLicences = {
  ski: LicenceLevel.None,
  snowboard: LicenceLevel.None,
}

export interface InstructorDto {
  id: string
  name: string
  phone?: string | null
  email?: string | null
  languages: InstructorLanguages
  licences: InstructorLicences
  salaryPrivate: number
  salaryGroup: number
  isActive: boolean
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

export interface CreateInstructorDto {
  name: string
  phone?: string
  email?: string
  languages?: InstructorLanguages
  licences?: InstructorLicences
  salaryPrivate?: number
  salaryGroup?: number
  isActive?: boolean
}

export interface UpdateInstructorDto {
  name?: string
  phone?: string
  email?: string
  languages?: InstructorLanguages
  licences?: InstructorLicences
  salaryPrivate?: number
  salaryGroup?: number
  isActive?: boolean
}

export interface ProductDto {
  id: string
  name: string
  lessonType: LessonType
  durationMinutes: number
  price: number
  pricePerAdditionalPerson: number | null
  vatRate: number
  color: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

export interface CreateProductDto {
  name: string
  lessonType: LessonType
  durationMinutes: number
  price: number
  pricePerAdditionalPerson?: number | null
  vatRate: number
  color?: string
  isActive?: boolean
}

export interface UpdateProductDto {
  name?: string
  lessonType?: LessonType
  durationMinutes?: number
  price?: number
  pricePerAdditionalPerson?: number | null
  vatRate?: number
  color?: string
  isActive?: boolean
}

// ─── Booking types ─────────────────────────────────────

export interface LessonSummaryDto {
  id: string
  date: string
  startTime: string
  endTime: string
  product?: ProductDto
}

export interface BookingDto {
  id: string
  customerId: string
  customer: CustomerDto
  lessonId: string
  skierName: string
  skierAge: number
  price: number
  vatRate: number
  basePrice: number
  vat: number
  receiptId: string | null
  receiptNumber: number | null
  lesson?: LessonSummaryDto
  createdAt: string
  updatedAt: string
}

export interface BookingEntryDto {
  customerId: string
  skiers: Skier[]
}

// ─── Lesson types ─────────────────────────────────────

export interface LessonDto {
  id: string
  date: string
  startTime: string
  endTime: string
  instructorIds: string[]
  instructors: InstructorDto[]
  productId?: string
  product?: ProductDto
  lessonType: LessonType
  requestId: string | null
  notes?: string | null
  status: LessonStatus
  paymentStatus: LessonPaymentStatus
  bookings: BookingDto[]
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

export interface CreateLessonDto {
  date: string
  startTime: string
  endTime: string
  instructorIds?: string[]
  productId: string
  bookings: BookingEntryDto[]
  requestId?: string
  notes?: string
}

export interface UpdateLessonDto {
  date?: string
  startTime?: string
  endTime?: string
  instructorIds?: string[]
  bookings?: BookingEntryDto[]
  notes?: string
  status?: LessonStatus
  productId?: string
}

// ─── Customer types ───────────────────────────────────

export interface CustomerDto {
  id: string
  name: string
  phone?: string
  email?: string
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

export interface CustomerSearchResultDto extends CustomerDto {
  lastLessonDate?: string | null
}

export interface CreateCustomerDto {
  name: string
  phone?: string
  email?: string
}

export interface UpdateCustomerDto {
  name?: string
  phone?: string
  email?: string
}

// ─── Request types ────────────────────────────────────

export enum Discipline {
  Ski = 'ski',
  Snowboard = 'snowboard',
}

export enum SkierLevel {
  CompleteBeginner = 'complete_beginner',
  Beginner = 'beginner',
  Intermediate = 'intermediate',
  UpperIntermediate = 'upper_intermediate',
}

export enum RequestStatus {
  New = 'new',
  Rejected = 'rejected',
  InProgress = 'in_progress',
  Done = 'done',
}

export interface Skier {
  name: string
  age: number
}

/** Empty skier for form default values (age as empty string for RHF compatibility) */
export const EMPTY_SKIER = { name: '', age: '' as unknown as number }

export interface LessonRequestDto {
  id: string
  lessonType: LessonType
  discipline: Discipline
  level: SkierLevel
  date: string
  time: string
  customerId: string
  customer: CustomerDto
  status: RequestStatus
  notes?: string
  skiers: Skier[]
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

export interface CreateLessonRequestDto {
  lessonType: LessonType
  discipline: Discipline
  level: SkierLevel
  date: string
  time: string
  customerId: string
  notes?: string
  skiers: Skier[]
}

export interface UpdateLessonRequestDto {
  lessonType?: LessonType
  discipline?: Discipline
  level?: SkierLevel
  date?: string
  time?: string
  customerId?: string
  status?: RequestStatus
  notes?: string
  skiers?: Skier[]
}

// ─── Receipt types ──────────────────────────────────────

export interface ReceiptDto {
  id: string
  receiptNumber: number
  status: ReceiptStatus
  paymentType: PaymentType
  posId: string
  customerId: string
  customer: CustomerDto
  bookings: BookingDto[]
  totalPrice: number
  bookingCount: number
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

export interface CreateReceiptDto {
  customerId: string
  bookingIds: string[]
  paymentType: PaymentType
  posId: string
}

// ─── Terminal Payment types ─────────────────────────────

export interface CreateTerminalPaymentRequest {
  price: number
  curr: 'CZK' | 'EUR'
  bookingIds: string[]
  customerId: string
  posId: string
}

// ─── Instructor Availability types ────────────────────

export interface InstructorAvailabilityDto {
  id: string
  instructorId: string
  date: string
  hour: number
}

export interface ToggleAvailabilityDto {
  instructorId: string
  date: string
  hour: number
}

export interface SetDayAvailabilityDto {
  instructorId: string
  date: string
  hours: number[]
}

export type AvailabilityMap = Record<string, number[]>

// ─── Instructor Salary types ─────────────────────────

export interface SalaryItemDto {
  id: string
  instructorId: string
  month: string
  description: string
  amount: number
}

export interface CreateSalaryItemDto {
  instructorId: string
  month: string
  description: string
  amount: number
}

export interface UpdateSalaryItemDto {
  description: string
  amount: number
}

export interface InstructorSalaryLessonDto {
  id: string
  date: string
  startTime: string
  endTime: string
  lessonType: string
  productName: string | null
  salary: number
}

export interface InstructorSalaryResponseDto {
  instructorName: string
  lessons: InstructorSalaryLessonDto[]
  salaryItems: SalaryItemDto[]
  totalLessonsSalary: number
  totalSalaryItems: number
  grandTotal: number
}

// ─── POS Closing types ─────────────────────────────────

export interface PosClosingVatSummaryDto {
  id: string
  vatRate: number
  totalBasePrice: number
  totalVat: number
  totalPrice: number
}

export interface PosClosingDto {
  id: string
  closingNumber: number
  posId: string
  totalCash: number
  totalCard: number
  totalInvoice: number
  receiptCount: number
  vatSummaries: PosClosingVatSummaryDto[]
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

export interface PosClosingPreviewDto {
  posId: string
  totalCash: number
  totalCard: number
  totalInvoice: number
  receiptCount: number
  vatSummaries: PosClosingVatSummaryDto[]
}

export interface CreatePosClosingDto {
  posId: string
}

export interface InstructorSalaryOverviewItemDto {
  instructorId: string
  instructorName: string
  totalLessonsSalary: number
  totalSalaryItems: number
  grandTotal: number
}

// ─── Timeline types ──────────────────────────────────────

export type TimelineItem = { type: 'receipt'; data: ReceiptDto } | { type: 'posClosing'; data: PosClosingDto }
